import React, { useState, useMemo, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import { getProgramById, getProgramsByLift, deleteCustomProgram } from '../data/programs';
import { set1RM, setSelectedProgram, saveSettings } from '../utils/settings';
import { calcDOTS, formatWeight } from '../utils/calculations';
import { addBodyweight, deleteBodyweight, setProgramProgress, exportAllData, importAllData, previewBackup } from '../db';
import ProgramBuilder from './ProgramBuilder';
import ProgramImporter from './ProgramImporter';

export default function ProfileTab({ settings, bodyweights, logEntries, prs, progress, refresh }) {
  const [bwInput, setBwInput] = useState('');
  const [showProgramSwap, setShowProgramSwap] = useState(null);
  const [confirmSwap, setConfirmSwap] = useState(null);
  const [editingRM, setEditingRM] = useState(null);
  const [rmInput, setRmInput] = useState('');
  const [showProgramDetails, setShowProgramDetails] = useState(false);
  const [confirmDeleteBW, setConfirmDeleteBW] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [buildingProgram, setBuildingProgram] = useState(false);
  const [importingProgram, setImportingProgram] = useState(false);
  const [confirmDeleteCustom, setConfirmDeleteCustom] = useState(null);
  const fileInputRef = useRef(null);

  const latestBW = bodyweights.length > 0 ? bodyweights[bodyweights.length - 1] : null;

  // Get best estimated 1RM per lift from log entries (the "display" 1RM)
  const bestEstimated = useMemo(() => {
    const best = { bench: 0, squat: 0, deadlift: 0 };
    for (const entry of logEntries) {
      if (entry.estimatedMax && entry.estimatedMax > best[entry.lift]) {
        best[entry.lift] = entry.estimatedMax;
      }
    }
    return best;
  }, [logEntries]);

  // Display 1RM: max of working max, best estimated, and any tested PRs
  const display1RM = useMemo(() => {
    const result = {};
    for (const lift of ['bench', 'squat', 'deadlift']) {
      const working = settings[`${lift}1RM`] || 0;
      const estimated = bestEstimated[lift] || 0;
      result[lift] = Math.max(working, estimated);
    }
    return result;
  }, [settings, bestEstimated]);

  const displayTotal = display1RM.bench + display1RM.squat + display1RM.deadlift;
  const dotsScore = latestBW ? calcDOTS(displayTotal, latestBW.weight) : 0;

  const bwChartData = useMemo(() => {
    return bodyweights.map(bw => ({
      date: bw.date,
      label: new Date(bw.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      weight: bw.weight,
    }));
  }, [bodyweights]);

  const handleAddBW = async () => {
    const w = parseFloat(bwInput);
    if (w > 0) {
      await addBodyweight(w);
      setBwInput('');
      refresh();
    }
  };

  const handleDeleteBW = async (id) => {
    await deleteBodyweight(id);
    setConfirmDeleteBW(null);
    refresh();
  };

  const handleRMSave = (lift) => {
    const v = parseFloat(rmInput);
    if (v >= 20) {
      set1RM(lift, v);
      setEditingRM(null);
      setRmInput('');
      refresh();
    }
  };

  const handleProgramSwap = async (lift, programId) => {
    setSelectedProgram(lift, programId);
    const prog = { weekIndex: 0, dayIndex: 0, cycle: 1 };
    const perDayProgs = ['bench_3x_beg', 'squat_3x_beg'];
    if (perDayProgs.includes(programId)) {
      const rm = settings[`${lift}1RM`] || 0;
      const round = settings.rounding || 2.5;
      const pcts = { 0: 0.70, 1: 0.75, 2: 0.80 };
      prog.dayWeights = {};
      for (const [d, pct] of Object.entries(pcts)) {
        prog.dayWeights[d] = Math.floor((rm * pct) / round) * round;
      }
    }
    await setProgramProgress(lift, prog);
    setConfirmSwap(null);
    setShowProgramSwap(null);
    refresh();
  };

  const handleExport = async () => {
    setExporting(true); setExportMsg(null);
    try {
      const data = await exportAllData();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sbs-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      saveSettings({ lastBackupSessionCount: logEntries.length });
      setExportMsg({ type: 'success', text: 'Backup exported successfully' });
    } catch (err) {
      setExportMsg({ type: 'error', text: `Export failed: ${err.message}` });
    } finally { setExporting(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportMsg(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        const preview = previewBackup(data);
        if (!preview) { setImportMsg({ type: 'error', text: 'Invalid backup file format' }); return; }
        setImportFile(data); setImportPreview(preview); setConfirmImport(true);
      } catch { setImportMsg({ type: 'error', text: 'Could not read file — invalid JSON' }); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!importFile || importing) return;
    setImporting(true);
    try {
      const counts = await importAllData(importFile);
      setImportMsg({ type: 'success', text: `Restored ${counts.logbook} sessions, ${counts.bodyweight} bodyweight entries, ${counts.accessoryLogs} accessory logs` });
      setConfirmImport(false); setImportPreview(null); setImportFile(null);
      refresh();
    } catch (err) {
      setImportMsg({ type: 'error', text: `Import failed: ${err.message}` });
    } finally { setImporting(false); }
  };

  // Imported templates generate one program per lift, so all three are
  // activated together and every lift restarts at week 1.
  const handleTemplateImport = async (generated) => {
    for (const lift of ['bench', 'squat', 'deadlift']) {
      const prog = generated[lift];
      if (!prog) continue;
      setSelectedProgram(lift, prog.id);
      await setProgramProgress(lift, { weekIndex: 0, dayIndex: 0, cycle: 1 });
    }
    setImportingProgram(false);
    refresh();
  };

  const handleDeleteCustomProg = (id) => {
    deleteCustomProgram(id);
    setConfirmDeleteCustom(null);
    refresh();
  };

  const liftColors = {
    bench: 'var(--bench-color)',
    squat: 'var(--squat-color)',
    deadlift: 'var(--deadlift-color)',
  };

  // === PROGRAM BUILDER MODE ===
  if (buildingProgram) {
    return (
      <ProgramBuilder
        settings={settings}
        onSave={(program) => {
          setBuildingProgram(false);
          refresh();
        }}
        onCancel={() => setBuildingProgram(false)}
      />
    );
  }

  // === PROGRAM IMPORTER MODE ===
  if (importingProgram) {
    return (
      <ProgramImporter
        settings={settings}
        onImport={handleTemplateImport}
        onCancel={() => setImportingProgram(false)}
      />
    );
  }

  return (
    <div>
      <div className="session-header">
        <h1>Profile</h1>
      </div>

      {/* 1RM - the number you'd tell someone */}
      <div className="card">
        <div className="card-header">1RM</div>
        {['bench', 'squat', 'deadlift'].map(lift => {
          const displayVal = display1RM[lift];
          const workingMax = settings[`${lift}1RM`] || 0;
          return (
            <div key={lift} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div className="profile-stat" style={{ borderBottom: 'none', padding: 0 }}>
                <span className="profile-stat-label" style={{ color: liftColors[lift], textTransform: 'capitalize' }}>
                  {lift}
                </span>
                {editingRM === lift ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="number" style={{ width: 80, padding: 6, textAlign: 'center' }}
                      value={rmInput} onChange={e => setRmInput(e.target.value)}
                      onFocus={e => e.target.select()} autoFocus />
                    <button className="btn btn-primary btn-small" style={{ width: 'auto', padding: '6px 12px' }}
                      onClick={() => handleRMSave(lift)}>✓</button>
                    <button className="btn btn-secondary btn-small" style={{ width: 'auto', padding: '6px 12px' }}
                      onClick={() => { setEditingRM(null); setRmInput(''); }}>✕</button>
                  </div>
                ) : (
                  <span className="profile-stat-value" onClick={() => {
                    setEditingRM(lift);
                    setRmInput(String(workingMax || ''));
                  }} style={{ cursor: 'pointer' }}>
                    {displayVal > 0 ? `${formatWeight(displayVal)}kg` : '—'}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {bestEstimated[lift] > 0 && bestEstimated[lift] >= workingMax
                  ? 'Estimated from performance'
                  : bestEstimated[lift] > 0
                    ? `Est: ${formatWeight(bestEstimated[lift])}kg from performance`
                    : 'No sessions logged yet'
                }
              </div>
            </div>
          );
        })}
        <div className="profile-stat">
          <span className="profile-stat-label">Total</span>
          <span className="profile-stat-value">{displayTotal > 0 ? `${formatWeight(displayTotal)}kg` : '—'}</span>
        </div>
        {dotsScore > 0 && (
          <div className="profile-stat">
            <span className="profile-stat-label" style={{ color: 'var(--dots-color)' }}>DOTS Score</span>
            <span className="profile-stat-value" style={{ color: 'var(--dots-color)' }}>{dotsScore.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* Program Details — collapsed, shows working max */}
      <div className="card">
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer',
        }} onClick={() => setShowProgramDetails(!showProgramDetails)}>
          <span className="card-header" style={{ marginBottom: 0 }}>Program Details</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>{showProgramDetails ? '−' : '+'}</span>
        </div>

        {showProgramDetails && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Working numbers used to calculate training weights. Managed automatically by auto-regulation.
            </div>
            {['bench', 'squat', 'deadlift'].map(lift => {
              const progId = settings[`${lift}Program`];
              const prog = getProgramById(progId);
              const liftProgress = progress[lift] || {};
              const isPerDay = ['bench_3x_beg', 'squat_3x_beg'].includes(progId);
              return (
                <div key={lift} style={{
                  padding: '10px 0', borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{
                        color: liftColors[lift], fontSize: 13, fontWeight: 600,
                        textTransform: 'uppercase', fontFamily: 'Barlow Condensed, sans-serif',
                      }}>{lift}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>
                        {prog?.displayName || 'Not set'}
                      </span>
                    </div>
                    <span style={{
                      fontFamily: 'Barlow Condensed, sans-serif', fontSize: 16,
                      fontWeight: 700, color: 'var(--text-dim)',
                    }}>{formatWeight(settings[`${lift}1RM`])}kg</span>
                  </div>
                  {isPerDay && liftProgress.dayWeights && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      Per-day: {Object.entries(liftProgress.dayWeights).map(([d, w]) =>
                        `D${parseInt(d) + 1}: ${formatWeight(w)}kg`
                      ).join(' · ')}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    Cycle {liftProgress.cycle || 1} · Week {(liftProgress.weekIndex || 0) + 1}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bodyweight */}
      <div className="card">
        <div className="card-header">Bodyweight</div>
        {latestBW && (
          <div className="profile-stat" style={{ borderBottom: 'none', paddingBottom: 0 }}>
            <span className="profile-stat-label">Current</span>
            <span className="profile-stat-value">{latestBW.weight}kg</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <input type="number" value={bwInput} onChange={e => setBwInput(e.target.value)}
            placeholder="Enter bodyweight (kg)" onFocus={e => e.target.select()}
            onKeyDown={e => e.key === 'Enter' && handleAddBW()} />
          <button className="btn btn-primary btn-small" style={{ width: 'auto', whiteSpace: 'nowrap' }}
            onClick={handleAddBW}>Add</button>
        </div>

        {bwChartData.length > 1 && (
          <div style={{ marginTop: 16 }}>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={bwChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="label" tick={{ fill: '#888', fontSize: 10 }} />
                <YAxis tick={{ fill: '#888', fontSize: 10 }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="weight" stroke="#ffffff" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {bodyweights.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="section-divider">History</div>
            {[...bodyweights].reverse().slice(0, 10).map((bw, i) => (
              <div key={bw.id || i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', fontSize: 13, color: 'var(--text-dim)',
                borderBottom: '1px solid var(--border)',
              }}>
                <span>{new Date(bw.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{bw.weight}kg</span>
                  <button style={{
                    background: 'none', border: 'none', color: 'var(--danger)',
                    fontSize: 13, cursor: 'pointer', padding: 4,
                  }} onClick={() => setConfirmDeleteBW(bw.id)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Programs */}
      <div className="card">
        <div className="card-header">Active Programs</div>
        {['bench', 'squat', 'deadlift'].map(lift => {
          const progId = settings[`${lift}Program`];
          const prog = getProgramById(progId);
          return (
            <div key={lift} style={{ marginBottom: 16 }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 6,
              }}>
                <span style={{
                  color: liftColors[lift], fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 14, fontWeight: 600, textTransform: 'uppercase',
                }}>{lift}</span>
                <button className="btn btn-secondary btn-small" style={{ width: 'auto', fontSize: 11 }}
                  onClick={() => setShowProgramSwap(showProgramSwap === lift ? null : lift)}>
                  Change
                </button>
              </div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{prog?.displayName || 'Not set'}</div>

              {showProgramSwap === lift && (
                <div style={{ marginTop: 12, padding: 12, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div className="section-divider" style={{ paddingTop: 0 }}>Select new program</div>
                  {getProgramsByLift(lift).map(p => {
                    const isActive = p.id === progId;
                    return (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center' }}>
                        <div className={`program-option ${isActive ? 'active-program' : ''}`} style={{ flex: 1 }}
                          onClick={() => {
                            if (!isActive) setConfirmSwap({ lift, programId: p.id, name: p.displayName });
                          }}>
                          <span className="program-option-name">
                            {p.displayName}
                            {p.isCustom && <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 6, fontWeight: 400 }}>custom</span>}
                          </span>
                          {isActive && <span className="program-option-badge">Active</span>}
                        </div>
                        {p.isCustom && !isActive && (
                          <button style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13, cursor: 'pointer', padding: '4px 8px' }}
                            onClick={() => setConfirmDeleteCustom(p.id)}>✕</button>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 6 }}>
                    <button className="btn btn-secondary btn-small" style={{ width: '100%', fontSize: 12 }}
                      onClick={() => { setShowProgramSwap(null); setBuildingProgram(true); }}>
                      + Create Custom Program
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <button className="btn btn-secondary btn-small" style={{ width: '100%', fontSize: 12 }}
            onClick={() => setImportingProgram(true)}>
            Import a Popular Program
          </button>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, textAlign: 'center' }}>
            Builds all three lifts from a known template
          </div>
        </div>
      </div>

      {/* Confirm swap modal */}
      {confirmSwap && (
        <div className="modal-overlay" onClick={() => setConfirmSwap(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Switch Program?</h3>
            <p>
              Switch {confirmSwap.lift} to <strong>{confirmSwap.name}</strong>?
              This will reset your progress to Week 1. Historical data is preserved.
            </p>
            <div className="btn-row">
              <button className="btn btn-secondary" onClick={() => setConfirmSwap(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => handleProgramSwap(confirmSwap.lift, confirmSwap.programId)}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete custom program */}
      {confirmDeleteCustom && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteCustom(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete Custom Program?</h3>
            <p>This will permanently delete this custom program. Training history is preserved.</p>
            <div className="btn-row">
              <button className="btn btn-secondary" onClick={() => setConfirmDeleteCustom(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
                onClick={() => handleDeleteCustomProg(confirmDeleteCustom)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete BW modal */}
      {confirmDeleteBW && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteBW(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete Entry?</h3>
            <p>Delete this bodyweight entry?</p>
            <div className="btn-row">
              <button className="btn btn-secondary" onClick={() => setConfirmDeleteBW(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
                onClick={() => handleDeleteBW(confirmDeleteBW)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="card">
        <div className="card-header">Settings</div>
        <div className="profile-stat">
          <span className="profile-stat-label">Weight Rounding</span>
          <span className="profile-stat-value">{settings.rounding || 2.5}kg</span>
        </div>
        <div className="profile-stat">
          <span className="profile-stat-label">Gender</span>
          <span className="profile-stat-value">Male</span>
        </div>
      </div>

      {/* Data Management */}
      <div className="card">
        <div className="card-header">Data</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
          Your training data is stored on this device only. Export regularly to protect against data loss.
        </div>
        <button className="btn btn-primary" style={{ marginBottom: 8 }}
          onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export All Data'}
        </button>
        <input type="file" accept=".json" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />
        <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
          Import Backup
        </button>
        {exportMsg && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 13,
            background: exportMsg.type === 'success' ? 'rgba(74,255,127,0.1)' : 'rgba(255,74,74,0.1)',
            border: `1px solid ${exportMsg.type === 'success' ? 'rgba(74,255,127,0.3)' : 'rgba(255,74,74,0.3)'}`,
            color: exportMsg.type === 'success' ? 'var(--success)' : 'var(--danger)',
          }}>{exportMsg.text}</div>
        )}
        {importMsg && !confirmImport && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 13,
            background: importMsg.type === 'success' ? 'rgba(74,255,127,0.1)' : 'rgba(255,74,74,0.1)',
            border: `1px solid ${importMsg.type === 'success' ? 'rgba(74,255,127,0.3)' : 'rgba(255,74,74,0.3)'}`,
            color: importMsg.type === 'success' ? 'var(--success)' : 'var(--danger)',
          }}>{importMsg.text}</div>
        )}
        {logEntries.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
            {logEntries.length} session{logEntries.length !== 1 ? 's' : ''} · {bodyweights.length} bodyweight entr{bodyweights.length !== 1 ? 'ies' : 'y'} · {prs.length} PR{prs.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Import confirmation modal */}
      {confirmImport && importPreview && (
        <div className="modal-overlay" onClick={() => { setConfirmImport(false); setImportPreview(null); setImportFile(null); }}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Restore Backup?</h3>
            <p style={{ marginBottom: 12 }}>
              This will <strong>replace all current data</strong> with the backup. This cannot be undone.
            </p>
            <div style={{
              background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
              marginBottom: 14, fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.6,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Backup contains
              </div>
              <div>{importPreview.logbook} session{importPreview.logbook !== 1 ? 's' : ''}</div>
              <div>{importPreview.bodyweight} bodyweight entr{importPreview.bodyweight !== 1 ? 'ies' : 'y'}</div>
              <div>{importPreview.prs} personal record{importPreview.prs !== 1 ? 's' : ''}</div>
              <div>{importPreview.accessoryExercises} accessor{importPreview.accessoryExercises !== 1 ? 'ies' : 'y'}</div>
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                Exported: {new Date(importPreview.exportDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <div className="btn-row">
              <button className="btn btn-secondary" onClick={() => { setConfirmImport(false); setImportPreview(null); setImportFile(null); }}>Cancel</button>
              <button className="btn btn-primary" style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
                onClick={handleImport} disabled={importing}>
                {importing ? 'Restoring...' : 'Replace All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
